import { useMemo, useRef, useState } from "react";
import api, { apiError, parseMoney, rows as toRows } from "@/lib/api";
import { toast } from "sonner";
import { FileUp, Upload, Truck, ArrowLeft } from "lucide-react";
import { Badge, Button, Card, PageHeader } from "@/components/ui-kit";
import ImportSummary from "./stock-import/ImportSummary";
import ImportRow from "./stock-import/ImportRow";
import ImportBills from "./stock-import/ImportBills";
import { resumo as calcResumo, pendencia, precisaAtencao } from "./stock-import/rows";
import { applyPricing, descontoEmReais, precoComMargem, totaisRateio } from "@/lib/purchasePricing";
import useDebounced from "@/hooks/useDebounced";

export default function StockImport() {
  const [step, setStep] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [supplierId, setSupplierId] = useState(null);
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  // Marcas já usadas no catálogo: viram o autocompletar do campo de marca de
  // cada linha, para "Mercur" e "MERCUR" não virarem duas marcas diferentes.
  const [marcas, setMarcas] = useState([]);
  const [totals, setTotals] = useState(null);
  const [nfe, setNfe] = useState(null);
  const [bills, setBills] = useState([]);
  const [soPendentes, setSoPendentes] = useState(false);
  const [margemMin, setMargemMin] = useState(65);
  const [margemMax, setMargemMax] = useState(85);
  // Cabeçalho de custos, editável. Nasce com o que a nota trouxe.
  const [custos, setCustos] = useState({ frete: 0, seguro: 0, outros: 0, desconto: 0 });
  const [tipoDesconto, setTipoDesconto] = useState("fixed");
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // O catálogo NÃO é mais baixado aqui. Antes vinham 100 produtos para
      // montar as opções do seletor, e o servidor casava contra o catálogo
      // inteiro: item casado fora dessas 100 aparecia em branco, como se o
      // vínculo tivesse falhado. Agora a busca é feita no servidor, sob demanda.
      // `/admin/categories` e não `/categories`: o endpoint público esconde as
      // categorias marcadas como fora da loja virtual, e é justamente nelas que
      // entram peça de reposição e item de uso interno — os que mais chegam por
      // nota de compra. Com a lista pública, a categoria certa sumia do seletor.
      const [{ data }, cats, brands] = await Promise.all([
        api.post("/admin/stock/import-xml", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        api.get("/admin/categories"),
        api.get("/admin/products/brands").catch(() => ({ data: [] })),
      ]);
      setCategories(toRows(cats));
      setMarcas(toRows(brands));
      setTotals(data.totals || null);
      // A NF traz o desconto em R$ (vDesc) — é o que o documento diz. Trocar
      // para % é decisão do operador.
      setCustos({
        frete: data.totals?.frete || 0,
        seguro: data.totals?.seguro || 0,
        outros: data.totals?.outros || 0,
        desconto: data.totals?.desconto || 0,
      });
      setTipoDesconto("fixed");
      setNfe(data.nfe || null);
      // Todas marcadas por padrão: a nota tem as parcelas porque elas serão
      // pagas — desmarcar é a exceção (boleto já quitado na entrega).
      setBills((data.bills || []).map((b) => ({ ...b, lancar: true })));
      setSupplier(data.supplier);
      setSupplierId(data.supplier_id);
      setRows(data.items.map((it) => ({
        ...it,
        action: it.variant_id ? "link" : "create",
        variant_id: it.variant_id || "",
        product_id: it.product_id || "",
        // O rótulo do casamento serve de valor inicial do seletor: sem ele o
        // campo ficaria vazio até alguém digitar uma busca.
        matched_product_label: it.matched_label ? it.matched_label.split(" · ")[0] : "",
        category_slug: "",
        // Nome do CATÁLOGO, já sem o lote que o fornecedor gruda na descrição.
        // Editável: o original continua à vista na linha da NF-e.
        name: it.suggested_name || it.name,
        xml_name: it.name,
        // Marca lida do XML quando o emissor mandou (a NF-e não tem campo
        // padrão — ver XmlImportController::extractBrand). Vazio aqui é comum,
        // e a linha oferece o campo com as marcas já usadas no autocompletar.
        brand: it.brand || "",
        // Margens padrão da loja. O preço sai da MÁXIMA e é recalculado ao
        // vivo; deixar o custo como preço (o que acontecia antes) gravava
        // margem zero em silêncio.
        margin_min_percentage: 65,
        margin_max_percentage: 85,
        price_manual: false,
        price: 0,
        qty: it.qty,
        // Quantidade original da nota: enquanto ela não muda, o total da
        // linha vem exato do servidor em vez de recalculado.
        qty_xml: it.qty,
        save_alias: true,
        revisado: false,
      })));
      setSoPendentes(false);
      setStep("review");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  /**
   * Atualiza uma linha.
   *
   * `__pick` é o retorno do ProductPicker, que entrega produto e variação
   * inteiros: guardamos os ids e os rótulos de uma vez, para o campo continuar
   * mostrando o escolhido mesmo depois de a busca mudar.
   */
  const setRow = (i, k, v) => setRows((prev) => prev.map((r, j) => {
    if (j !== i) return r;
    // Preço digitado à mão passa a mandar sobre a margem daquela linha.
    if (k === "price") return { ...r, price: v, price_manual: true };
    if (k !== "__pick") return { ...r, [k]: v };

    return {
      ...r,
      product_id: v.product?.id || "",
      variant_id: v.variant?.id || "",
      matched_product_label: v.product?.name || "",
      matched_variant_label: v.variant?.label || "",
      // Escolha manual encerra a dúvida do palpite: quem escolheu foi a pessoa.
      revisado: true,
      via: v.product ? r.via : null,
      matched_label: v.product && v.variant ? `${v.product.name} · ${v.variant.label}` : null,
    };
  }));

  const marcarRevisado = (i) => setRow(i, "revisado", true);

  const aplicarCategoria = (slug) => {
    if (!slug) return;
    setRows((prev) => prev.map((r) => (r.action === "create" ? { ...r, category_slug: slug } : r)));
    toast.success("Categoria aplicada aos itens novos");
  };

  const aplicarMargem = () => {
    // Reaplica as duas margens e devolve o preço ao cálculo automático: é o que
    // o operador espera de um "aplicar a todos" depois de ter mexido em algumas.
    setRows((prev) => prev.map((r) => (r.action === "create" ? {
      ...r,
      margin_min_percentage: margemMin,
      margin_max_percentage: margemMax,
      price_manual: false,
    } : r)));
    toast.success(`Margens de ${margemMin}% e ${margemMax}% aplicadas sobre o custo`);
  };

  // Recálculo ao vivo. Debounce só no CABEÇALHO: ele move todas as linhas, e a
  // cada tecla isso é O(n) sobre a nota inteira. As margens de item mexem numa
  // linha só e respondem na hora.
  const custosDebounced = useDebounced(custos, 150);
  const tipoDebounced = useDebounced(tipoDesconto, 150);

  const precificados = useMemo(
    () => {
      const calculados = applyPricing(rows, custosDebounced, { discount_type: tipoDebounced });

      /* Preço e piso derivam das margens da LINHA sobre o custo recalculado.
         Preço digitado à mão vence a margem: quem digitou foi a pessoa.

         O VÍNCULO fica de fora do preço automático. A margem existe para
         precificar produto NOVO; num item que já está no catálogo, aplicá-la
         sozinha reprecificaria a etiqueta que está na prateleira toda vez que
         alguém mexesse no frete — e agora que o preço do vínculo é enviado ao
         servidor, isso viraria alteração de verdade, em silêncio, em dezenas de
         produtos. Ali o preço só existe quando alguém digita.

         O PISO continua sendo calculado nos dois: ele acompanha o custo por
         definição, e um piso do custo antigo não protege margem nenhuma. */
      return calculados.map((r) => ({
        ...r,
        price: r.action === "link"
          ? (r.price_manual ? r.price : "")
          : (r.price_manual ? r.price : precoComMargem(r.unit_cost, r.margin_max_percentage)),
        min_price: precoComMargem(r.unit_cost, r.margin_min_percentage),
      }));
    },
    [rows, custosDebounced, tipoDebounced],
  );

  const baseProdutos = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.v_prod) || 0), 0),
    [rows],
  );
  const descontoEfetivo = descontoEmReais({
    desconto: custos.desconto, tipo: tipoDesconto, baseTotal: baseProdutos,
  });
  const rateio = useMemo(() => totaisRateio(precificados), [precificados]);

  const resumo = useMemo(() => calcResumo(precificados), [precificados]);
  const visiveis = useMemo(
    () => precificados.map((r, i) => ({ r, i })).filter(({ r }) => !soPendentes || precisaAtencao(r)),
    [precificados, soPendentes],
  );

  const confirm = async () => {
    // A primeira linha bloqueada leva o operador até ela, em vez de um toast
    // genérico que obriga a caçar o erro numa nota de quarenta itens.
    const ruim = precificados.findIndex((r) => pendencia(r));
    if (ruim >= 0) {
      setSoPendentes(true);
      toast.error(`${precificados[ruim].name}: ${pendencia(precificados[ruim])}`);
      return;
    }

    setSaving(true);
    try {
      const items = precificados.map((r) => {
        if (r.action === "skip") return { action: "skip", cProd: r.cProd, qty: 0 };
        if (r.action === "create") {
          return {
            action: "create", cProd: r.cProd, ean: r.ean, qty: parseInt(r.qty, 10) || 0,
            name: r.name, brand: r.brand?.trim() || null,
            category_slug: r.category_slug, price: parseMoney(r.price),
            // O valor da nota é CUSTO. Vai separado do preço de venda para a
            // margem do cadastro de produto ter com o que ser calculada.
            cost_price: Number(r.unit_cost ?? r.unit_value) || null,
            v_prod: Number(r.v_prod) || null,
            v_ipi: Number(r.v_ipi) || 0, v_icms_st: Number(r.v_icms_st) || 0,
            margin_min_percentage: r.margin_min_percentage, margin_max_percentage: r.margin_max_percentage,
            min_price: Number(r.min_price) || null,
            ncm: r.ncm, cest: r.cest, cfop: r.cfop, unidade: r.unidade, origem: r.origem,
            icms_cst: r.icms_cst, icms_aliquota: r.icms_aliquota,
            pis_cst: r.pis_cst, pis_aliquota: r.pis_aliquota,
            cofins_cst: r.cofins_cst, cofins_aliquota: r.cofins_aliquota,
            save_alias: r.save_alias,
          };
        }
        // O vínculo leva os dados fiscais junto: o servidor completa o que
        // faltar no cadastro do produto existente (e só o que faltar).
        return {
          action: "link", variant_id: r.variant_id, cProd: r.cProd, ean: r.ean,
          // Marca vai também no vínculo: o servidor só grava se o produto
          // estiver sem marca — é como o catálogo antigo, cadastrado à mão antes
          // de a coluna existir, se completa sozinho a cada compra.
          brand: r.brand?.trim() || null,
          qty: parseInt(r.qty, 10) || 0, save_alias: r.save_alias,
          cost_price: Number(r.unit_cost ?? r.unit_value) || null,
          /* Preço de venda só vai quando alguém DIGITOU um. Vazio significa
             "não mexer": reprecificar sozinho, em cima de uma nota, mudaria a
             etiqueta de produto que já está na prateleira. */
          price: parseMoney(r.price) > 0 ? parseMoney(r.price) : null,
          v_prod: Number(r.v_prod) || null,
          v_ipi: Number(r.v_ipi) || 0, v_icms_st: Number(r.v_icms_st) || 0,
          min_price: Number(r.min_price) || null,
          ncm: r.ncm, cest: r.cest, cfop: r.cfop, unidade: r.unidade, origem: r.origem,
          icms_cst: r.icms_cst, icms_aliquota: r.icms_aliquota,
          pis_cst: r.pis_cst, pis_aliquota: r.pis_aliquota,
          cofins_cst: r.cofins_cst, cofins_aliquota: r.cofins_aliquota,
        };
      });
      const { data } = await api.post("/admin/stock/import-xml/confirm", {
        supplier, items,
        // O cabeçalho vai junto e o SERVIDOR refaz a conta. O que a tela
        // calculou é pré-visualização.
        costs: custos,
        discount_type: tipoDesconto,
        nfe_key: nfe?.key || null,
        bills: bills.filter((b) => b.lancar).map((b) => ({
          number: b.number, due_date: b.due_date, amount: parseMoney(b.amount),
          nfe_number: nfe?.number || null,
        })),
      });
      const extra = [
        data.enriched ? `${data.enriched} cadastro(s) completado(s)` : null,
        data.bills ? `${data.bills} conta(s) a pagar lançada(s)` : null,
      ].filter(Boolean);
      const sufixo = extra.length ? ` · ${extra.join(" · ")}` : "";
      toast.success(`Importado: ${data.created} novo(s), ${data.updated} atualizado(s), ${data.skipped} ignorado(s)${sufixo}`);
      setStep("upload"); setRows([]); setSupplier(null); setBills([]); setNfe(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (step === "upload") {
    return (
      <div className="space-y-5 max-w-2xl" data-testid="stock-import-page">
        <PageHeader
          eyebrow="Operação"
          title="Importar estoque via XML"
          icon={FileUp}
          description="Importação em duas etapas: você envia o XML e depois confere item a item. Nada é gravado no estoque antes de confirmar."
        />
        <Card className="p-8 text-center">
          <FileUp size={40} className="mx-auto text-slate-300 mb-3" aria-hidden />
          <p className="font-medium text-slate-700">Envie o XML da NF-e de compra</p>
          <p className="text-sm text-slate-400 mt-1 mb-5">Vamos casar cada item com o seu catálogo e dar entrada no estoque físico.</p>
          <Button size="lg" icon={Upload} loading={uploading} onClick={() => fileRef.current?.click()} data-testid="stock-import-pick">
            {uploading ? "Lendo XML..." : "Selecionar XML"}
          </Button>
          <input ref={fileRef} type="file" accept=".xml,text/xml,application/xml" onChange={upload}
            aria-label="Escolher arquivo XML da nota" className="hidden" data-testid="stock-import-file" />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="stock-import-review">
      <PageHeader
        eyebrow="Operação"
        title="Revisar importação"
        icon={FileUp}
        description="Cada linha escolhe o seu destino: vincular soma na variação existente, criar cadastra o produto e ignorar deixa a linha de fora."
        actions={(
          <>
            <Button variant="secondary" icon={ArrowLeft} onClick={() => setStep("upload")} data-testid="stock-import-back">Voltar</Button>
            <Button onClick={confirm} loading={saving} data-testid="stock-import-confirm"
              title={resumo.bloqueadas ? `${resumo.bloqueadas} linha(s) com campo obrigatório em branco` : undefined}>
              {saving ? "Importando..." : "Confirmar entrada"}
            </Button>
          </>
        )}
      />

      {supplier && (
        <Card className="p-4 flex items-center gap-3 text-sm" data-testid="stock-import-supplier">
          <Truck size={18} className="text-brand-600 shrink-0" aria-hidden />
          <div>
            <p className="font-medium text-slate-900 flex items-center gap-2">
              {supplier.name || "Fornecedor (NF-e)"}
              {supplierId && <Badge tone="success">já cadastrado</Badge>}
            </p>
            <p className="text-xs text-slate-400">CNPJ {supplier.cnpj || "—"} · {[supplier.city, supplier.state].filter(Boolean).join("/")}</p>
          </div>
        </Card>
      )}

      <ImportBills bills={bills} nfe={nfe} onChange={setBills} total={totals?.nota || 0} />

      <ImportSummary
        resumo={resumo}
        totaisNota={totals}
        soPendentes={soPendentes}
        onTogglePendentes={() => setSoPendentes((v) => !v)}
        categorias={categories}
        categoriaEmMassa=""
        onCategoriaEmMassa={aplicarCategoria}
        margemMin={margemMin}
        onMargemMin={setMargemMin}
        margemMax={margemMax}
        onMargemMax={setMargemMax}
        onAplicarMargem={aplicarMargem}
        custos={custos}
        onCusto={(k, v) => setCustos((c) => ({ ...c, [k]: v }))}
        tipoDesconto={tipoDesconto}
        onTipoDesconto={setTipoDesconto}
        descontoEfetivo={descontoEfetivo}
        rateio={rateio}
        temNovos={resumo.criar > 0}
      />

      {/* Grade de revisão, não listagem: cada célula é um campo do rascunho.
          Fica em <table> própria porque o modo cartão do DataTable renderiza as
          colunas como pares rótulo/valor de leitura — um seletor de produto não
          cabe ali. */}
      {/* Um datalist só para a nota inteira: cada linha aponta para ele em vez
          de repetir a lista de marcas quarenta vezes no DOM. */}
      {marcas.length > 0 && (
        <datalist id="stock-import-marcas">
          {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
        </datalist>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Itens da NF-e e o destino de cada um no catálogo</caption>
          <thead>
            <tr className="text-left text-eyebrow uppercase text-slate-500 border-b border-slate-200">
              <th scope="col" className="px-4 py-3">Item da NF-e</th>
              <th scope="col" className="px-3 py-3 text-center">Qtd</th>
              <th scope="col" className="px-3 py-3">Ação</th>
              <th scope="col" className="px-4 py-3">Destino no catálogo</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map(({ r, i }) => (
              <ImportRow key={i} r={r} i={i} categorias={categories} marcas={marcas}
                onChange={setRow} onRevisar={marcarRevisado} />
            ))}
          </tbody>
        </table>
        {visiveis.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid="stock-import-none-pending">
            Nenhuma linha pendente — tudo pronto para confirmar.
          </p>
        )}
      </Card>
    </div>
  );
}
